'use client';

import { useCallback, useRef } from 'react';

import { CasinoAction } from './casinoReducer';
import { GameQueryParams } from '../interfaces/query.interface';

export function useCasinoGames(dispatch: React.Dispatch<CasinoAction>) {
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastFetchParamsRef = useRef<string>('');

  const fetchGames = useCallback(async (reset = false, customParams?: GameQueryParams) => {
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });

      const defaultParams: GameQueryParams = {
        quantity: 50,
        offset: 0,
        sortBy: 'newest',
      };

      const finalParams = { ...defaultParams, ...customParams };

      const params = new URLSearchParams();
      Object.entries(finalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
          params.append(key, stringValue);
        }
      });

      const url = `/api/casino/games?${params.toString()}`;

      const response = await fetch(url, {
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (reset) {
        dispatch({ type: 'SET_GAMES', payload: { games: data.games, reset: true } });
        dispatch({ type: 'SET_PAGINATION', payload: { currentPage: 0 } });
      } else {
        dispatch({ type: 'SET_GAMES', payload: { games: data.games, reset: false } });
      }

      dispatch({
        type: 'SET_PAGINATION',
        payload: {
          totalCount: data.totalCount,
          hasMore: data.hasMore,
        },
      });
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        dispatch({ type: 'SET_ERROR', payload: err.message });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [dispatch]);

  return {
    fetchGames,
    abortControllerRef,
    lastFetchParamsRef,
  };
}
